import { gql } from '@apollo/client';
import { logger } from '@tock/logging';
import { useCollectTimerDataMutation } from 'generated/graphql';

gql`
  mutation CollectTimerData($durationSeconds: Float!, $metricName: String!) {
    collectTimerData(durationSeconds: $durationSeconds, metricName: $metricName)
  }
`;

// TODO: Rename component and interfaces to be more generic if we continue to send data to Prometheus as well as GA
export interface GATimerCommands {
  start: (metricName: string, timerMetadata?: TimerMetadata) => void;
  finish: (
    metricName: string,
    metadata: { category: string; label?: string; cutoffMs?: number },
    timerMetadata?: TimerMetadata
  ) => void;
  clear: (metricName: string, timerMetadata?: TimerMetadata) => void;
}

interface TimerMetadata {
  /**
   * A string to be used as a unique identifier for a metric.
   * This identifier is NOT ultimately sent to the server / our metrics database, but can be used to associate
   * certain start / finish actions with a given series of events (e.g., if a purchase is created, and later renders on Timeline)
   */
  identifier?: string;
}

/**
 * Adds an identifier suffix to a metric name
 */
function getMetricNameWithIdentifier(metricName: string, identifier?: string) {
  return identifier ? `${metricName}-${identifier}` : metricName;
}

/**
 * Hook to create a timer that can be used to record how long an action takes,
 * even across different components. This handles measuring the actual duration
 * as well as collecting the data so it can be viewed in Google Analytics.
 *
 * While playing with this, try looking at the output in the verbose console as
 * a sanity check that things are happening at the correct time. Search the
 * console for the name of your metric to filter out the noise.
 *
 * @example
 *
 * // IN COMPONENT A:
 * const gaTimer = useGATimer();
 * gaTimer.start('Switched date on flex Timeline');
 *
 * // IN COMPONENT B:
 * const gaTimer = useGATimer();
 * gaTimer.finish('Timeline', 'Switched date on flex Timeline');
 */
export function useGATimer(): GATimerCommands {
  const [collectTimerData] = useCollectTimerDataMutation();

  return {
    start: (metricName: string, timerMetadata?: TimerMetadata) => {
      const metricNameWithId = getMetricNameWithIdentifier(metricName, timerMetadata?.identifier);

      try {
        // Starting a metric that's already been started will restart it.
        performance.clearMarks(metricNameWithId);
        performance.clearMeasures(metricNameWithId);
        logger.debug(`Started measuring ${metricNameWithId}`);
        performance.mark(metricNameWithId);
      } catch {
        // We don't want to impact the app if this fails.
        window.LOGGER?.warn('Failed to start measuring timer data', { metricNameWithId });
      }
    },
    clear: (metricName: string, timerMetadata?: TimerMetadata) => {
      const metricNameWithId = getMetricNameWithIdentifier(metricName, timerMetadata?.identifier);

      try {
        performance.clearMarks(metricNameWithId);
        performance.clearMeasures(metricNameWithId);
        logger.debug(`Cleared measurement ${metricNameWithId}`);
      } catch {
        // We don't want to impact the app if this fails.
        window.LOGGER?.warn('Failed to clear timer data', { metricNameWithId });
      }
    },
    finish: (
      metricName: string,
      eventMetadata: { category: string; label?: string; cutoffMs?: number },
      timerMetadata?: TimerMetadata
    ) => {
      const metricNameWithId = getMetricNameWithIdentifier(metricName, timerMetadata?.identifier);

      try {
        // Finishing a metric that hasn't been started (or has already finished) is a no-op.
        if (performance.getEntriesByName(metricNameWithId).length === 1) {
          const durationMs = Math.round(
            performance.measure(metricNameWithId, metricNameWithId).duration
          );
          performance.clearMarks(metricNameWithId);
          performance.clearMeasures(metricNameWithId);
          logger.debug(`Finished measuring ${metricNameWithId}: Took ${durationMs}ms`);

          eventMetadata.cutoffMs ??= 60_000;
          const { cutoffMs } = eventMetadata;

          if (durationMs <= cutoffMs) {
            collectTimerData({
              variables: {
                durationSeconds: durationMs / 1000,
                metricName: metricName.replace(/([^a-z]+)/gi, '_').toLowerCase(),
              },
            });
          } else {
            /**
             * Google analytics only shows averages for user timing metrics, not
             * percentiles. Because of this, an individual data point that's an
             * extreme outlier can skew an average to the point where the data
             * is no longer useful. We mitigate this by throwing out data points
             * that exceed 60 seconds. Individual metrics can pass in a
             * different cutoff threshold if necessary.
             */
            window.LOGGER?.debug('User timing metric exceeded cutoff time', {
              metricNameWithId,
              durationMs,
              cutoffMs,
            });
          }
        }
      } catch (exception) {
        const errorMessage = exception instanceof Error ? exception.message : 'Unknown error';
        // We don't want to impact the app if this fails.
        window.LOGGER?.warn('Failed to stop measuring timer data', {
          metricNameWithId,
          errorMessage,
        });
      }
    },
  };
}
